// @ts-nocheck suppress errors caused by zE - rm to check new code
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLazyLoadScript } from "@/client/services/hooks/useLazyLoad";
import { Company, User } from "@/client/types/AuthConfig";
import { useConfigStore } from "@/client/services/state/configStore";
import { useConsentStore } from "@/client/services/state/consentStore";
import OutlineButton from "@/client/components/shared/buttons/OutlineButton";

interface ZendeskWidgetProps {
  user: User;
  company: Company;
  setZendeskOpen: (zendeskOpen: boolean) => void;
}

export default function ZendeskWidget({
  user,
  company,
  setZendeskOpen,
}: ZendeskWidgetProps) {
  const { t } = useTranslation();
  const { config } = useConfigStore();
  const key = config.zdKey;
  const functionalAllowed = useConsentStore((s) => s.categories.functional);
  const openPreferences = useConsentStore((s) => s.openPreferences);
  const url = "https://static.zdassets.com/ekr/snippet.js?key=" + key;

  const configure = () => {
    const brandColor = "#00BCF1";
    zE("webWidget", "updateSettings", {
      webWidget: {
        color: {
          theme: brandColor,
          launcher: brandColor, // This will also update the badge
          launcherText: "#ffffff",
          button: brandColor,
          resultLists: brandColor,
          header: brandColor,
          articleLinks: brandColor,
        },
      },
    });

    if (user.email) {
      const name = user.first_name + " " + user.last_name;
      const { email } = user;
      const organization = (company && company.name) || "n/a";

      zE("webWidget", "identify", { name, email, organization });
      zE("webWidget", "prefill", {
        name: { value: name, readOnly: true },
        email: { value: email, readOnly: true },
        organization: { value: organization, readOnly: true },
      });
    }
  };

  const display = () => {
    zE("webWidget", "toggle");
    zE("webWidget", "show");
  };

  // zESettings must be set before the Zendesk snippet executes; this effect is
  // declared before useLazyLoadScript so it runs first on each commit.
  useEffect(() => {
    if (!functionalAllowed) {
      setZendeskOpen(false);
      return undefined;
    }
    window.zESettings = {
      webWidget: { contactForm: { attachments: true } },
    };
    const zdOnLoad = window.setInterval(() => {
      if (typeof zE !== "undefined" && typeof zE.activate !== "undefined") {
        clearInterval(zdOnLoad);
        zE("webWidget", "hide");
        zE("webWidget:on", "close", () => {
          zE("webWidget", "hide");
          setZendeskOpen(false);
        });
        configure();
        display();
      }
    }, 30);
    return () => clearInterval(zdOnLoad);
    // reason: re-run only when functional consent flips
  }, [functionalAllowed]); // eslint-disable-line react-hooks/exhaustive-deps

  // Loads the snippet only when functional consent is granted (canFetch gate).
  useLazyLoadScript(url, functionalAllowed, null, { name: "id", value: "ze-snippet" });

  if (!functionalAllowed) {
    return (
      <OutlineButton size="sm" onClick={openPreferences}>
        {t('consent.chat.enablePrompt')}
      </OutlineButton>
    );
  }

  return <></>;
}
